import {
	DeleteOutlined,
	EditOutlined,
	PlusOutlined,
	QuestionCircleOutlined,
} from "@ant-design/icons";
import {
	Button,
	Collapse,
	Flex,
	Form,
	Input,
	InputNumber,
	Switch,
	TimePicker,
	Tooltip,
	theme,
} from "antd";
import { isValidHour, isValidMinute } from "common/datetime";
import dayjs from "dayjs";
import { useRef, useState } from "react";

const handleStopPropagation = (e) => {
	e.stopPropagation();
};

const DEFAULT_CANCEL_CONDITIONS = [
	{
		name: "状態 1",
		day: 0,
		hour: 18,
		minute: 0,
		refundPercentage: 0,
	},
	{
		name: "状態 2",
		day: 0,
		hour: 18,
		minute: 0,
		refundPercentage: 100,
	},
	{
		name: "状態 3",
		day: 0,
		hour: 18,
		minute: 0,
		refundPercentage: 50,
	},
	{
		name: "状態 4",
		day: 0,
		hour: 18,
		minute: 0,
		refundPercentage: 20,
	},
];

const LabelName = ({ name }) => {
	const form = Form.useFormInstance();
	const [isEditing, setIsEditing] = useState(false);
	const [inputValue, setInputValue] = useState(form.getFieldValue(name));
	const ref = useRef(null);

	const onClick = (e) => {
		handleStopPropagation(e);
		setIsEditing(true);
		ref?.current?.focus();
	};

	const handleEditDone = () => {
		setIsEditing(false);
		form.setFieldValue(name, inputValue);
	};

	return (
		<Flex gap={15}>
			<Input
				ref={ref}
				size={"small"}
				variant={isEditing ? "filled" : "borderless"}
				readOnly={!isEditing}
				onBlur={handleEditDone}
				value={inputValue}
				onChange={(e) => setInputValue(e.target.value)}
				style={{
					cursor: !isEditing ? "pointer" : "auto",
				}}
				onClick={!isEditing ? onClick : undefined}
			/>

			<Button
				onClick={onClick}
				size="small"
				type="text"
				icon={<EditOutlined />}
			>
				編集
			</Button>
		</Flex>
	);
};

const CategoryCancelConditions = () => {
	const form = Form.useFormInstance();
	const colorPrimary = theme.useToken().token.colorPrimary;

	return (
		<>
			<div className="flex justify-between items-center h-8">
				<Flex gap={10} align="center">
					このイベントの独自キャンセル設定
					<Tooltip
						title={
							<div>
								<div>OFFの場合は[設定]ページの登録内容が踏襲されます。</div>
							</div>
						}
						overlayInnerStyle={{ width: 300 }}
					>
						<QuestionCircleOutlined
							style={{
								color: colorPrimary,
								marginRight: 9,
							}}
						/>
					</Tooltip>
					<Form.Item
						labelCol={{ span: 24 }}
						wrapperCol={{ span: 24 }}
						className="mb-0"
						name="cancelable"
					>
						<Switch
							// value={cancelable}
							onChange={(val) => {
								form.setFieldValue("cancelable", val);
								if (!form.getFieldValue("cancelConditions")?.length) {
									form.setFieldValue(
										"cancelConditions",
										DEFAULT_CANCEL_CONDITIONS,
									);
								}
							}}
						/>
					</Form.Item>
				</Flex>
				<Form.List name="cancelConditions">
					{(fields, { add }) => (
						<Form.Item
							noStyle
							shouldUpdate={(prevValues, currentValues) =>
								prevValues.cancelable !== currentValues.cancelable
							}
						>
							{({ getFieldValue }) => {
								const cancelable = getFieldValue("cancelable");
								return cancelable ? (
									<Button
										size="small"
										type="primary"
										icon={<PlusOutlined />}
										// onClick={() => {
										//   add({
										//     name: `状態 ${fields.length + 1}`,
										//     day: 0,
										//     hour: 18,
										//     minute: 0,
										//     refundPercentage: 0,
										//   })
										// }}
										disabled
									>
										追加する
									</Button>
								) : null;
							}}
						</Form.Item>
					)}
				</Form.List>
			</div>

			<Form.List name="cancelConditions">
				{(fields, { remove }) => (
					<Form.Item
						noStyle
						shouldUpdate={(prevValues, currentValues) =>
							prevValues.cancelable !== currentValues.cancelable
						}
					>
						{({ getFieldValue }) => {
							const cancelable = getFieldValue("cancelable");
							return cancelable ? (
								<>
									<Collapse
										size="small"
										// className="mb-6"
										items={fields.map((field, index) => {
											return {
												extra: (
													<Flex gap={10}>
														<Button
															size="small"
															type="text"
															icon={<DeleteOutlined />}
															danger
															onClick={(e) => {
																handleStopPropagation(e);

																remove(index);

																if (fields.length - 1 === 0) {
																	form.setFieldValue("cancelable", false);
																}
															}}
														>
															削除
														</Button>
													</Flex>
												),
												key: [
													"cancelConditions",
													field.name,
													"name",
													index,
												].join("."),
												label: (
													<LabelName
														name={["cancelConditions", field.name, "name"]}
													/>
												),
												children: (
													<>
														<Form.Item
															{...field}
															name={[field.name, "day"]}
															label="日数（予約より何日前までキャンセル可能）"
															rules={[
																{
																	required: true,
																	message: "キャンセル可能日は必須です",
																},
															]}
														>
															<InputNumber
																placeholder="例：1"
																addonBefore="予約より"
																addonAfter="日前"
																min={0}
																type="number"
																onPressEnter={(e) => {
																	e.preventDefault();
																}}
															/>
														</Form.Item>
														<Form.Item
															noStyle
															shouldUpdate={(prevValues, currValues) =>
																`${prevValues.cancelConditions[index]?.hour},${prevValues.cancelConditions[index]?.minute}` !==
																`${currValues.cancelConditions[index]?.hour},${currValues.cancelConditions[index]?.minute}`
															}
														>
															{({ getFieldValue }) => {
																const hour = getFieldValue([
																	"cancelConditions",
																	field.name,
																	"hour",
																]);
																const minute = getFieldValue([
																	"cancelConditions",
																	field.name,
																	"minute",
																]);

																const getTime = (hour, minute) => {
																	if (
																		!isValidHour(hour) ||
																		!isValidMinute(minute)
																	) {
																		return "";
																	}

																	return dayjs().hour(hour).minute(minute);
																};

																return (
																	<Form.Item
																		// {...field}
																		name={[field.name, "hour"]}
																		label="時間（何時何分までキャンセル可能）"
																		getValueProps={() => ({
																			value: getTime(hour, minute),
																		})}
																		rules={[
																			{
																				required: true,
																				message: "キャンセル可能時間は必須です",
																			},
																		]}
																	>
																		<TimePicker
																			placeholder="時間"
																			popupClassName="hide-timepicker-footer"
																			format="HH:mm"
																			inputReadOnly
																			showNow={false}
																			allowClear={false}
																			hideDisabledOptions={true}
																			minuteStep={5}
																			style={{ width: 140 }}
																			onCalendarChange={(time) => {
																				form.setFieldValue(
																					[
																						"cancelConditions",
																						field.name,
																						"hour",
																					],
																					+dayjs(time).format("HH"),
																				);
																				form.setFieldValue(
																					[
																						"cancelConditions",
																						field.name,
																						"minute",
																					],
																					+dayjs(time).format("m"),
																				);
																			}}
																		/>
																	</Form.Item>
																);
															}}
														</Form.Item>
														<Form.Item
															noStyle
															shouldUpdate={(prevValues, currentValues) =>
																prevValues.fee !== currentValues.fee
															}
														>
															{({ getFieldValue }) => {
																const fee = parseFloat(getFieldValue("fee"));
																return fee ? (
																	<Form.Item
																		name={[field.name, "refundPercentage"]}
																		label="キャンセル後の払い戻し率"
																		rules={[
																			{
																				required: true,
																				message: "キャンセル可能日は必須です",
																			},
																		]}
																	>
																		<InputNumber
																			placeholder="例：1"
																			addonAfter="%"
																			min={0}
																			max={100}
																			type="number"
																			onPressEnter={(e) => {
																				e.preventDefault();
																			}}
																			style={{ width: 140 }}
																		/>
																	</Form.Item>
																) : null;
															}}
														</Form.Item>
													</>
												),
											};
										})}
									/>
								</>
							) : null;
						}}
					</Form.Item>
				)}
			</Form.List>

			<Form.Item
				shouldUpdate={(prevValues, currentValues) =>
					prevValues.cancelable !== currentValues.cancelable
				}
				noStyle
			>
				{({ getFieldValue }) => {
					const cancelable = getFieldValue("cancelable");

					if (!cancelable) return null;

					return (
						<Form.Item
							name="cancelDescription"
							label="キャンセルに関する説明文"
							rules={[
								{
									required: false,
									message: "キャンセルに関する説明文は必須です",
								},
							]}
							className="[&_.ant-form-item-label>label]:h-8"
						>
							<Input.TextArea
								placeholder="キャンセルに関する説明文を入力してください"
								autoSize={{ minRows: 2 }}
							/>
						</Form.Item>
					);
				}}
			</Form.Item>
		</>
	);
};

export default CategoryCancelConditions;
